import React, { useState, useEffect } from "react";
import { api } from "~/api";
import { KnowledgeJsonFile, KnowledgeListItem, DifyKnowledgeDocumentItem } from "shared/Schemas";
import styles from "~/css/KnowledgePage.module.css";
import "bootstrap/dist/css/bootstrap.min.css";

/**
 * ナレッジページコンポーネント
 * - ナレッジ登録、更新、削除、および関連データの管理を行う
 */
const KnowledgePage: React.FC = () => {
  const [jsonFiles, setJsonFiles] = useState<KnowledgeJsonFile[]>([]); // JSONファイルリスト
  const [selectedFile, setSelectedFile] = useState<string>(""); // 選択中のJSONファイル
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle"); // 現在の状態
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeListItem[]>([]); // ナレッジ一覧
  const [selectedItems, setSelectedItems] = useState<string[]>([]); // 選択中のナレッジID
  const [statusMessage, setStatusMessage] = useState<string>(""); // 状態メッセージ
  const [expandedDatasets, setExpandedDatasets] = useState<Record<string, boolean>>({}); // 展開状態
  const [documentsMap, setDocumentsMap] = useState<
    Record<string, DifyKnowledgeDocumentItem[] | null>
  >({}); // datasetId -> documents
  const [loadingDocs, setLoadingDocs] = useState<Record<string, boolean>>({});
  let isSubmitting = false; // 連続クリック防止用フラグ

  /**
   * 初回レンダリング時のデータ取得
   * - ナレッジ一覧を取得し、画面に表示
   * - JSONファイル一覧を取得し、選択可能にする
   */
  useEffect(() => {
    getKnowledgeList();
    fetchJsonFiles();
  }, []);

  /**
   * ナレッジ一覧を取得する関数
   * - APIからすべてのナレッジ項目をページング形式で取得
   * - 状態を更新して表示
   */
  const getKnowledgeList = async () => {
    try {
      let allData: KnowledgeListItem[] = [];
      let page = 1;
      const limit = 100;

      while (true) {
        const response = await api.dify.getKnowledgeList.query({ page, limit });
        allData = [...allData, ...response.data];
        if (response.data.length < limit) break; // 最終ページ
        page++;
      }
      setKnowledgeList(allData); // データを更新
      console.info("Fetched all knowledge data:", allData);

      if (allData.length > 0) {
        // すべてのデータセットIDに対してドキュメントIDを取得
        for (const dataset of allData) {
          const datasetId = dataset.id;
          // console.log("取得したナレッジID:", datasetId);

          if (datasetId) {
            try {
              // 各データセットのドキュメントIDを取得
              await api.dify.getDocumentsByDatasetId.query({ datasetId: datasetId });

              // 取得したドキュメントIDをコンソールに出力
              // console.log(`ナレッジID ${datasetId} のドキュメントID一覧:`, documentResponse);
            } catch (docError) {
              console.error(`データセット ${datasetId} のドキュメントID取得に失敗:`, docError);
            }
          } else {
            console.error(`データセットIDが無効です: ${dataset}`);
          }
        }
      } else {
        console.error("ナレッジリストが空です。");
      }

      return allData;
    } catch (error) {
      console.error("Error fetching knowledge list:", error);
      return [];
    }
  };

  /**
   * dataset を展開してドキュメント一覧を取得する
   */
  const toggleDatasetExpand = async (datasetId: string) => {
    const currently = !!expandedDatasets[datasetId];
    if (currently) {
      setExpandedDatasets((p) => ({ ...p, [datasetId]: false }));
      return;
    }

    // 展開要求: まだ取得していなければロードして格納
    if (!documentsMap[datasetId]) {
      setLoadingDocs((p) => ({ ...p, [datasetId]: true }));
      try {
        const res = await api.dify.getDocumentsByDatasetId.query({ datasetId });
        setDocumentsMap((p) => ({ ...p, [datasetId]: res.data || [] }));
      } catch (e) {
        console.error("ドキュメント取得に失敗:", e);
        setDocumentsMap((p) => ({ ...p, [datasetId]: [] }));
      } finally {
        setLoadingDocs((p) => ({ ...p, [datasetId]: false }));
      }
    }

    setExpandedDatasets((p) => ({ ...p, [datasetId]: true }));
  };

  /**
   * ドキュメント単位で更新を行う
   */
  const updateSingleDocument = async (datasetId: string, documentId: string) => {
    setStatus("loading");
    setStatusMessage("");
    try {
      // サーバー側のエンドポイントを呼び出して、Difyへの更新はサーバーで行う
      console.info("Requesting server to reindex document (disable->wait->enable)", {
        datasetId,
        documentId,
      });
      await api.dify.reindexDocuments.mutate({
        datasetId,
        documentIds: [documentId],
        maxRetries: 12,
        intervalMs: 5000,
      });

      console.info("Document reindex request completed via server:", { datasetId, documentId });
      setStatus("success");
      // 有効化が完了したことを明示的に通知する（WebSocket不要）
      setStatusMessage(`ドキュメントが有効化されました: ${documentId}`);
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (e) {
      console.error("ドキュメント更新エラー:", e);
      setStatus("error");
      setStatusMessage("ドキュメント更新に失敗しました。ログを確認してください。");
      setTimeout(() => setStatusMessage(""), 3000);
    } finally {
      setStatus("idle");
    }
  };

  /**
   * 行単位でナレッジ（データセット）を更新する
   * 選択した 1 件のみを再取得して更新 API を叩く
   */
  const updateSingleKnowledge = async (datasetId: string) => {
    setStatus("loading");
    setStatusMessage("");
    try {
      const selectedKnowledgeItem = knowledgeList.find((item) => item.id === datasetId);
      if (!selectedKnowledgeItem) {
        throw new Error("該当するナレッジが見つかりません");
      }

      // ドキュメント一覧を取得して、サーバー側で disable->wait->enable を実行
      const documentResponse = await api.dify.getDocumentsByDatasetId.query({ datasetId });
      const documentIds = documentResponse.data.map((doc: DifyKnowledgeDocumentItem) => doc.id);

      if (!documentIds || documentIds.length === 0) {
        setStatus("error");
        setStatusMessage("ドキュメントが見つかりませんでした。");
        setTimeout(() => setStatusMessage(""), 3000);
        return;
      }

      // サーバーに再インデックス要求を出す（サーバー側でリトライ/待機を行う）
      await api.dify.reindexDocuments.mutate({
        datasetId,
        documentIds,
        maxRetries: 12,
        intervalMs: 5000,
      });

      setStatus("success");
      setStatusMessage("ナレッジ内のドキュメントを再インデックスしました。");
      // 最新のナレッジ一覧を再取得
      await getKnowledgeList();
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("行単位の更新エラー:", error);
      setStatus("error");
      setStatusMessage("更新に失敗しました。エラーを確認してください。");
      setTimeout(() => setStatusMessage(""), 3000);
    } finally {
      setStatus("idle");
    }
  };

  /**
   * JSONファイル一覧を取得する関数
   * - サーバー上にあるJSONファイルを取得し、ドロップダウンで選択可能にする
   */
  const fetchJsonFiles = async () => {
    try {
      const files = await api.dify.getJsonFiles.query();
      setJsonFiles(files);
      console.info("jsonFiles:", files);
    } catch (error) {
      console.error("Error fetching JSON files:", error);
    }
  };

  /**
   * UNIXタイムスタンプをフォーマットして表示
   * - YYYY/MM/DD AMorPM HH:MM の形式で出力
   */
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /**
   * ドロップダウンから選択されたファイルの変更処理
   * - 選択されたファイルパスをstateに保存
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFile(event.target.value);
  };

  /**
   * Submitボタンのクリック処理
   * - 選択されたJSONファイルをナレッジとして登録
   * - 成功時にメッセージ表示および選択リセット
   */
  const handleButtonClick = async () => {
    if (isSubmitting) {
      return; // 既にリクエスト中の場合は処理を中断
    }

    if (!selectedFile) {
      alert("ファイルを選択してください");
      return;
    }

    isSubmitting = true; // リクエスト中を示す
    setStatus("loading"); // 状態を「loading」に設定
    setStatusMessage(""); // メッセージをリセット

    try {
      console.info("Selected File:", selectedFile);
      // ファイルパスを事前に取得
      const filePath = selectedFile;

      // ナレッジをDifyへ保存
      await api.dify.knowledge.mutate(filePath);

      // 登録成功時の状態更新
      setStatus("success");
      setStatusMessage("ナレッジが登録されました！");
      setSelectedFile(""); // 選択ファイルをリセット

      // 最新のナレッジ一覧を再取得して画面を更新
      await getKnowledgeList();

      // 3秒後にメッセージをクリア
      setTimeout(() => {
        setStatusMessage("");
      }, 3000);
    } catch (error) {
      setStatus("error");
      setStatusMessage("API呼び出し中にエラーが発生しました。");
      console.error("Error during submit:", error);
      // エラー時も3秒後にメッセージをクリア
      setTimeout(() => {
        setStatusMessage("");
      }, 3000);
    } finally {
      isSubmitting = false; // 処理完了後にフラグをリセット
    }
  };

  /**
   * 全選択/全解除の切り替え
   * - 現在の選択状態に基づき、すべてのナレッジを選択または解除する
   */
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = knowledgeList.map((item) => item.id);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  /**
   * 選択されたナレッジを削除
   * - 削除対象を確認後、APIを使って順に削除
   * - 削除後、最新のナレッジ一覧を再取得
   */
  const deleteSelectedKnowledge = async () => {
    if (selectedItems.length === 0) {
      alert("削除するナレッジを選択してください。");
      return;
    }
    const confirmDelete = window.confirm("選択された項目を削除します。本当に削除しますか？");
    if (!confirmDelete) return;

    setStatus("loading");
    try {
      for (const datasetId of selectedItems) {
        await api.dify.deleteKnowledge.mutate({ datasetId: datasetId });
      }
      setStatus("success");
      setStatusMessage("削除が完了しました。");
      setSelectedItems([]); // 選択状態のリセット

      // 最新の一覧を再取得して state を更新
      await getKnowledgeList();
    } catch (error) {
      setStatus("error");
      setStatusMessage("削除中にエラーが発生しました。");
      console.error("Error deleting knowledge:", error);
    } finally {
      // 3秒後にメッセージをクリアし、状態をidleに戻す
      setTimeout(() => {
        setStatusMessage("");
        setStatus("idle");
      }, 3000);
    }
  };

  /**
   * 個別のナレッジ項目の選択切り替え
   * - 指定したナレッジIDを選択または解除
   */
  const handleSelectItem = (datasetId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems((prev) => [...prev, datasetId]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== datasetId));
    }
  };

  /**
   * 更新ボタンのクリック処理
   * - 選択されたJSONファイルをナレッジ更新
   * - 成功時にメッセージ表示および選択リセット
   */
  const handleUpdate = async () => {
    if (selectedItems.length === 0) {
      alert("更新するナレッジを選択してください。");
      return;
    }

    setStatus("loading");
    setStatusMessage(""); // メッセージをリセット
    try {
      const selectedKnowledgeItems = await Promise.all(
        selectedItems.map(async (datasetId) => {
          const selectedKnowledgeItem = knowledgeList.find((item) => item.id === datasetId);
          if (!selectedKnowledgeItem) return null;

          // 各データセットのドキュメントIDを取得
          const documentResponse = await api.dify.getDocumentsByDatasetId.query({
            datasetId: datasetId,
          });
          const documentIds = documentResponse.data.map((doc: DifyKnowledgeDocumentItem) => doc.id);
          // ファイル名はナレッジの登録名（selectedKnowledgeItem.name）に基づいて決定
          const filePath = `${selectedKnowledgeItem.name}.json`;

          return {
            datasetId: datasetId,
            documentIds: documentIds,
            filePath: filePath,
          };
        })
      );

      // null を除外
      const validItems: { datasetId: string; documentIds: string[]; filePath: string }[] =
        selectedKnowledgeItems.filter(
          (item): item is { datasetId: string; documentIds: string[]; filePath: string } =>
            item !== null
        );

      console.info("📤 送信するデータ:", JSON.stringify(validItems, null, 2));

      // 更新処理の実行
      const response = await api.dify.updateKnowledge.mutate({ knowledgeItems: validItems });
      console.info("更新が成功しました:", response);

      setStatus("success");
      setStatusMessage("更新が成功しました！データベースが更新されました。");
      setSelectedItems([]); // 選択状態をリセット

      // 最新のナレッジ一覧を再取得
      await getKnowledgeList();

      // 一定時間後にメッセージをクリア（ここでは3秒後）
      setTimeout(() => {
        setStatusMessage("");
      }, 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setStatus("error");
      setStatusMessage("更新に失敗しました。エラー詳細を確認してください。");

      // エラーの場合も一定時間後にメッセージをクリア
      setTimeout(() => {
        setStatusMessage("");
      }, 3000);
    } finally {
      setStatus("idle"); // 処理完了後の状態に戻す
    }
  };

  // 一括再インデックス：すべてのナレッジを順に処理してドキュメント単位で再インデックスする
  const bulkReindexAll = async () => {
    if (!knowledgeList || knowledgeList.length === 0) {
      alert("ナレッジが存在しません。");
      return;
    }

    setStatus("loading");
    setStatusMessage("");

    try {
      for (const item of knowledgeList) {
        const datasetId = item.id;
        try {
          const docRes = await api.dify.getDocumentsByDatasetId.query({ datasetId });
          const documentIds = (docRes.data || []).map((d: DifyKnowledgeDocumentItem) => d.id);
          if (documentIds.length === 0) {
            console.info(`No documents for dataset ${datasetId}`);
            continue;
          }

          // サーバー側で disable->wait->enable を行う
          await api.dify.reindexDocuments.mutate({ datasetId, documentIds });
          console.info(`Reindexed dataset ${datasetId} (${documentIds.length} documents)`);
        } catch (e) {
          console.error(`Failed to reindex dataset ${item.id}:`, e);
        }
      }

      setStatus("success");
      setStatusMessage("一括再インデックスが完了しました。");
      await getKnowledgeList();
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (e) {
      console.error("一括再インデックスでエラー:", e);
      setStatus("error");
      setStatusMessage("一括再インデックスに失敗しました。ログを確認してください。");
      setTimeout(() => setStatusMessage(""), 3000);
    } finally {
      setStatus("idle");
    }
  };

  const isAllSelected = knowledgeList.length > 0 && selectedItems.length === knowledgeList.length;

  return (
    <div className={styles.fullscreenContainer}>
      <div className={`container ${styles.container}`}>
        <div className={`${styles.controller} form-group d-flex align-items-center`}>
          <label htmlFor="jsonFileSelect" className="form-label me-2">
            Select JSON file:
          </label>
          <select
            id="jsonFileSelect"
            value={selectedFile}
            onChange={handleFileChange}
            className={styles.jsonSelector}
            style={{ minWidth: "200px" }}
            disabled={status === "loading"}
          >
            <option value="">--Select a file--</option>
            {Array.isArray(jsonFiles) &&
              jsonFiles.map((file) => {
                const displayName = file.name.replace(/\.json$/, "");
                const isDisabled = knowledgeList.some((item) => item.name === displayName);
                return (
                  <option key={file.path} value={file.path} disabled={isDisabled}>
                    {displayName} {isDisabled ? "(登録済み)" : ""}
                  </option>
                );
              })}
          </select>
          <button
            onClick={handleButtonClick}
            className="btn btn-primary"
            disabled={status === "loading"}
          >
            Submit
          </button>
        </div>
        <div className={styles.statusMessage}>
          {status === "loading" && <p className={styles.loading}>登録中...</p>}
          {statusMessage && <p className={styles.success}>{statusMessage}</p>}
        </div>
        <div className={styles.controller}>
          <button
            onClick={deleteSelectedKnowledge}
            className="btn btn-danger"
            disabled={status === "loading"}
          >
            削除
          </button>
        </div>
        <div className={styles.controller}>
          <button
            onClick={handleUpdate} // 引数を渡さずに関数を直接呼び出す
            className="btn btn-warning"
          >
            JSON再登録
          </button>
        </div>
        <div className={styles.controller}>
          <button
            onClick={async () => await bulkReindexAll()}
            className="btn btn-secondary"
            disabled={status === "loading"}
          >
            一括再インデックス
          </button>
        </div>

        {/* ナレッジリストを表示 */}
        <div className={styles.scroll}>
          {Array.isArray(knowledgeList) && knowledgeList.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">
                    <input type="checkbox" onChange={handleSelectAll} checked={isAllSelected} />
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    番号
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    登録名
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    ID
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    追加日
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    更新日
                  </th>
                  <th scope="col" style={{ opacity: 0.5 }}>
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {knowledgeList.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleDatasetExpand(item.id)}
                        title="クリックでドキュメントを表示/非表示"
                      >
                        {expandedDatasets[item.id] ? "▾ " : "▸ "}
                        {item.name}
                      </td>
                      <td>{item.id}</td>
                      <td>{formatDate(Number(item.created_at))}</td>
                      <td>{formatDate(Number(item.updated_at))}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={async () => {
                            // 行単位でナレッジのドキュメントを再インデックス（disable->wait->enable）
                            await updateSingleKnowledge(item.id);
                          }}
                          disabled={status === "loading"}
                        >
                          ナレッジ再インデックス
                        </button>
                      </td>
                    </tr>
                    {expandedDatasets[item.id] &&
                      (documentsMap[item.id] && documentsMap[item.id]!.length > 0 ? (
                        documentsMap[item.id]!.map((doc) => (
                          <tr key={`${item.id}-doc-${doc.id}`} className="table-light">
                            <td />
                            <td />
                            <td style={{ paddingLeft: "24px", fontSize: "0.95em" }}>
                              {doc.name || doc.id}
                            </td>
                            <td style={{ opacity: 0.8 }}>{doc.id}</td>
                            <td />
                            <td />
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={async () => await updateSingleDocument(item.id, doc.id)}
                                disabled={status === "loading"}
                              >
                                ドキュメント再インデックス
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr key={`${item.id}-doc-empty`} className="table-light">
                          <td />
                          <td />
                          <td colSpan={5} style={{ fontStyle: "italic" }}>
                            {loadingDocs[item.id] ? "読み込み中..." : "ドキュメントがありません"}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            status === "success" && <p>No data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgePage;
